<script setup lang="ts">
import { ElMessage, ElMessageBox } from "element-plus";
import _ from "lodash";
import { computed, onMounted, ref } from "vue";

import { ListDefinition, ListVisibility, SpecialList } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { listVisibilityDescriptions } from "~/utils/enumDescriptions";
import { showError } from "~/utils/handleAPIError";

import { useMyInfoStore } from "~/stores/MyInfoStore";
import LabelledOptionsEditor from "./LabelledOptionsEditor.vue";

type mainType = ListDefinition;

const props = defineProps<{
  formDefinitionGuid?: string;
  workflowDefinitionGuid?: string;
  editingList?: mainType;
}>();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: mainType): void;
  (e: "added", value: mainType): void;
  (e: "deleted", value: mainType): void;
}>();

const drawerOpen = defineModel<boolean>({ required: true });

const myInfoStore = useMyInfoStore();

const lastSavedJson = ref<string>("");
const localEditingList = ref<ListDefinition>({} as ListDefinition);
const newListVisibility = ref<ListVisibility>();
const editorOpen = ref(false);

const pdfCodeListCount = ref<number>(0);

const getNumberOfPdfCodesForWorkflow = async () => {
  try {
    const response = await apiClient.listDefinition.getPdfCodeListDefinitions(
      props.workflowDefinitionGuid,
      myInfoStore.currentSysOrgGuid,
    );
    pdfCodeListCount.value = response.length;
  } catch (error) {
    showError(
      `Failed to fetch workflow definition for GUID ${props.workflowDefinitionGuid}:`,
      error,
    );
    throw error;
  }
};

const visibilityOptionsToShow = computed(() => {
  const allOptions = Object.values(ListVisibility);
  let options = allOptions;

  if (!myInfoStore.isSuperAdmin) {
    options = options.filter((option) => option !== ListVisibility.PUBLIC);
  }

  if (props.workflowDefinitionGuid && !props.formDefinitionGuid) {
    options = options.filter(
      (option) => option !== ListVisibility.FORM_SPECIFIC,
    );
  }

  if (props.editingList && !myInfoStore.isSuperAdmin) {
    // hierarchy order (lower index = lower hierarchy)
    const hierarchyOrder = [
      ListVisibility.FORM_SPECIFIC,
      ListVisibility.WORKFLOW_SPECIFIC,
      ListVisibility.ORG_WIDE,
      ListVisibility.PUBLIC,
    ];

    const currentHierarchyIndex = hierarchyOrder.indexOf(
      props.editingList.visibility,
    );

    // Filter out options with lower hierarchy than current
    options = options.filter((option) => {
      const optionHierarchyIndex = hierarchyOrder.indexOf(option);
      return optionHierarchyIndex >= currentHierarchyIndex;
    });
  }

  return options;
});

const canSave = computed(() => {
  return (
    saveNeeded.value &&
    localEditingList.value?.name &&
    localEditingList.value?.items?.length > 0 &&
    localEditingList.value?.items.every((item) => item.label && item.value)
  );
});

const saveNeeded = computed(() => {
  const changed =
    JSON.stringify(localEditingList.value) !== lastSavedJson.value;

  return changed;
});

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(localEditingList.value);
};

const handleCreateListDefinition = async () => {
  await apiClient.listDefinition
    .createListDefinition(localEditingList.value)
    .then((res) => {
      emit("added", res);
      ElMessage.success("List created successfully");
      handleCloseListEditor();
    })
    .catch((error) => {
      showError(error);
      return [];
    });
};

const handleUpdateListDefinition = async () => {
  await apiClient.listDefinition
    .updateListDefinition(
      localEditingList.value.listDefinitionGuid,
      localEditingList.value,
    )
    .then((res) => {
      emit("updated", res);
      ElMessage.success("List updated successfully");
      handleCloseListEditor();
    })
    .catch((error) => {
      showError(error);
      return [];
    });
};

const handleDelete = async (list: ListDefinition) => {
  await ElMessageBox.confirm(
    `Are you sure you want to delete this list?`,
    `Deleting '${list.name}'`,
    {
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  )
    .then(async () => {
      await apiClient.listDefinition
        .deleteListDefinition(list.listDefinitionGuid)
        .then(() => {
          emit("deleted", list);
          ElMessage.success("List deleted successfully");
          handleCloseListEditor();
        })
        .catch((error) => {
          showError(error);
          return [];
        });
    })
    .catch(() => {
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

/**
 * If the list has a formDefinitionGuid, it is form specific.
 * If the list has an orgGuid, it is org-wide.
 * If the list has neither, it is public.
 *
 * This function updates the visibility of the list based on the selected option in the editor.
 */
const updateVisibility = () => {
  // Set the visibility property based on the selected radio option
  localEditingList.value.visibility = newListVisibility.value!;

  // Set the appropriate guids based on the visibility
  if (newListVisibility.value === ListVisibility.PUBLIC) {
    localEditingList.value.formDefinitionGuid = undefined;
    localEditingList.value.sysOrgGuid = undefined;
    localEditingList.value.workflowDefinitionGuid = undefined;
  } else if (newListVisibility.value === ListVisibility.ORG_WIDE) {
    localEditingList.value.formDefinitionGuid = undefined;
    localEditingList.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
    localEditingList.value.workflowDefinitionGuid = undefined;
  } else if (newListVisibility.value === ListVisibility.WORKFLOW_SPECIFIC) {
    localEditingList.value.formDefinitionGuid = undefined;
    localEditingList.value.workflowDefinitionGuid =
      props.workflowDefinitionGuid;
    localEditingList.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
  } else if (newListVisibility.value === ListVisibility.FORM_SPECIFIC) {
    localEditingList.value.formDefinitionGuid = props.formDefinitionGuid;
    localEditingList.value.workflowDefinitionGuid =
      props.workflowDefinitionGuid;
    localEditingList.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
  }
};

const handleCloseListEditor = () => {
  // Initialize with default values
  localEditingList.value = {
    // @ts-expect-error - we want the server to generate a new guid
    listDefinitionGuid: undefined,
    name: "",
    items: [],
    formDefinitionGuid: props.formDefinitionGuid,
    workflowDefinitionGuid: props.workflowDefinitionGuid,
    sysOrgGuid: myInfoStore.currentSysOrgGuid,
    visibility: props.formDefinitionGuid
      ? ListVisibility.FORM_SPECIFIC
      : ListVisibility.WORKFLOW_SPECIFIC,
  };

  // Reset the visibility selection
  newListVisibility.value = props.formDefinitionGuid
    ? ListVisibility.FORM_SPECIFIC
    : ListVisibility.WORKFLOW_SPECIFIC;

  editorOpen.value = false;
  emit("closeDrawer");
};

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
    })
      .then(() => {
        // do nothing
      })
      .catch(() => {
        ElMessage({
          type: "info",
          message: "Canceled",
        });
        handleCloseListEditor();
        emit("closeDrawer");
      });
  } else {
    handleCloseListEditor();
    emit("closeDrawer");
  }
};

const getDrawerTitle = () => {
  return localEditingList.value.listDefinitionGuid
    ? `Editing List - ${localEditingList.value.name}`
    : "Creating New List";
};

onMounted(() => {
  if (props.editingList) {
    // Clone the existing list
    localEditingList.value = _.cloneDeep(props.editingList);

    // important
    if (!localEditingList.value.items) {
      localEditingList.value.items = [];
    }

    // For legacy lists without sysOrgGuid, populate it so updates succeed
    // (only for form/workflow specific lists, not public ones)
    if (
      !localEditingList.value.sysOrgGuid &&
      (localEditingList.value.formDefinitionGuid ||
        localEditingList.value.workflowDefinitionGuid)
    ) {
      localEditingList.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;
    }

    // Determine the visibility based on the guids
    if (localEditingList.value.formDefinitionGuid) {
      newListVisibility.value = ListVisibility.FORM_SPECIFIC;
    } else if (localEditingList.value.workflowDefinitionGuid) {
      newListVisibility.value = ListVisibility.WORKFLOW_SPECIFIC;
    } else if (localEditingList.value.sysOrgGuid) {
      newListVisibility.value = ListVisibility.ORG_WIDE;
    } else {
      newListVisibility.value = ListVisibility.PUBLIC;
    }

    // If there's an explicit visibility set, use that
    if (localEditingList.value.visibility) {
      newListVisibility.value = localEditingList.value.visibility;
    }
  } else {
    // Initialize a new list with default values
    localEditingList.value = {
      // @ts-expect-error - we want the server to generate a new guid
      listDefinitionGuid: undefined,
      name: "",
      items: [],
      formDefinitionGuid: props.formDefinitionGuid,
      workflowDefinitionGuid: props.workflowDefinitionGuid,
      sysOrgGuid: myInfoStore.currentSysOrgGuid,
      visibility: props.formDefinitionGuid
        ? ListVisibility.FORM_SPECIFIC
        : ListVisibility.WORKFLOW_SPECIFIC,
    };

    // default to most restrictive visibility
    if (props.formDefinitionGuid) {
      newListVisibility.value = ListVisibility.FORM_SPECIFIC;
    } else {
      newListVisibility.value = ListVisibility.WORKFLOW_SPECIFIC;
    }
  }

  getNumberOfPdfCodesForWorkflow();

  updateLastUpdated();
});
</script>

<template>
  <el-drawer
    v-if="localEditingList"
    :model-value="drawerOpen"
    :title="getDrawerTitle()"
    :before-close="handleClose"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
  >
    <div class="ListEditor">
      <div class="buttons">
        <el-button
          type="primary"
          plain
          :disabled="!canSave"
          @click="
            localEditingList.listDefinitionGuid
              ? handleUpdateListDefinition()
              : handleCreateListDefinition()
          "
        >
          Save
        </el-button>
      </div>

      <el-form label-position="left" class="new-list-form" label-width="150px">
        <el-form-item label="Name">
          <el-input
            v-model="localEditingList.name"
            placeholder="Enter list name"
            class="name"
            clearable
          />
        </el-form-item>

        <el-form-item label="Visibility">
          <el-radio-group
            v-model="newListVisibility"
            @change="updateVisibility"
          >
            <el-radio
              v-for="option in visibilityOptionsToShow"
              :key="option"
              :value="option"
            >
              {{ listVisibilityDescriptions[option].label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          v-if="
            localEditingList.visibility === ListVisibility.WORKFLOW_SPECIFIC
          "
          label="Special List?"
        >
          <el-select
            v-model="localEditingList.specialList"
            placeholder="Select special list"
            popper-class="ListEditorPopper"
            clearable
          >
            <template v-for="option in SpecialList" :key="option">
              <el-option
                :label="option"
                :value="option"
                :disabled="
                  option === SpecialList.PDF_CODE && pdfCodeListCount > 0
                "
              />
              <span
                v-if="option === SpecialList.PDF_CODE && pdfCodeListCount > 0"
                :key="option + '-warn'"
                class="pdfWarning"
              >
                (Only one PDF Code list is allowed per Workflow.)
              </span>
            </template>
          </el-select>
        </el-form-item>

        <el-form-item label="Options">
          <LabelledOptionsEditor v-model="localEditingList.items" />
        </el-form-item>
        <el-button
          v-if="localEditingList.listDefinitionGuid"
          type="danger"
          class="delete-button"
          @click="handleDelete(localEditingList)"
        >
          Delete
        </el-button>
      </el-form>
    </div>
  </el-drawer>
</template>

<style lang="less">
.ListEditor {
  display: grid;
  gap: 1em;
  .new-list-form {
    .delete-button {
      margin-top: 30px;
    }
  }
  .buttons {
    justify-self: end;
  }

  .el-form-item {
    margin-bottom: 1em;
  }

  .name {
    width: 300px;
  }

  .el-radio-group {
    display: grid;
  }
  .el-select {
    width: 300px;
  }

  .comment {
    font-size: 0.9rem;
    margin: 0 0 0 1em;
    color: var(--el-color-primary-light-3);
  }
}
.ListEditorPopper {
  .pdfWarning {
    color: #f56c6c;
    margin: 0 20px;
    font-size: 12px;
    vertical-align: middle;
  }
}
</style>
